/**
 * What the Live Promote sidebar reads and does: preparing the protocol, a scenario's decisions on a write, the
 * protocol's promotion history, its template generation, and promoting a change of settings, which asks the person
 * to confirm anything switched on before it is Ready.
 */

import { useCallback, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import { atomWithMutation, atomWithQuery } from 'jotai-tanstack-query';
import { toast } from '@/components/ui/sonner';
import { promotionKey, usePromoteProtocol, usePromotionPreview } from '@/hooks/use-promotion';
import { invalidateQueries } from '@/lib/query-client';
import { getApiErrorMessage } from '@/lib/utils';
import type { NotReadyItem, ScenarioSetting } from '@/modules/promotion/types';
import { promotionService } from '@/services/promotion.service';
import { livePromotionService } from '@/services/live-promotion.service';
import { liveKey } from '../use-live-pool';

const JOB_POLL_MS = 4000;

const prepareMutationAtom = atomWithMutation(() => ({
	mutationKey: promotionKey('live', 'prepare'),
	mutationFn: ({ sopId }: { sopId: string }) => livePromotionService.prepare(sopId),
	onSettled: (_view, _error, variables) => {
		invalidateQueries(liveKey(variables.sopId));
		invalidateQueries(promotionKey('live-job', variables.sopId));
	}
}));

/** Starts Promote's preparation of the protocol: types if none, the live sort, then the replies. */
export function usePrepare() {
	const [mutation] = useAtom(prepareMutationAtom);
	return mutation;
}

const decisionsQueryAtom = (sopId: string, typeId: string, tool: string) =>
	atomWithQuery(() => ({
		queryKey: liveKey(sopId, 'decisions', typeId, tool),
		queryFn: () => livePromotionService.getDecisions(sopId, typeId, tool),
		enabled: Boolean(sopId && typeId && tool),
		staleTime: 30 * 1000
	}));

/** One scenario's supervisor decisions on one write, newest first. */
export function useDecisions(sopId: string, typeId: string, tool: string) {
	const atom = useMemo(() => decisionsQueryAtom(sopId, typeId, tool), [sopId, typeId, tool]);
	const [query] = useAtom(atom);
	return query;
}

const historyQueryAtom = (sopId: string) =>
	atomWithQuery(() => ({
		queryKey: promotionKey('history', sopId),
		queryFn: () => promotionService.history(sopId),
		enabled: Boolean(sopId),
		staleTime: 0
	}));

export function usePromotionHistory(sopId: string) {
	const atom = useMemo(() => historyQueryAtom(sopId), [sopId]);
	const [query] = useAtom(atom);
	return query;
}

const jobQueryAtom = (sopId: string) =>
	atomWithQuery(() => ({
		queryKey: promotionKey('live-job', sopId),
		queryFn: () => promotionService.getJob(sopId),
		enabled: Boolean(sopId),
		staleTime: 0,
		refetchInterval: (query: { state: { data?: { status: string } | null } }) => {
			const status = query.state.data?.status;
			return status === 'queued' || status === 'running' ? JOB_POLL_MS : false;
		}
	}));

/** The protocol's latest template generation, read again while it runs. */
export function useLiveJob(sopId: string) {
	const atom = useMemo(() => jobQueryAtom(sopId), [sopId]);
	const [query] = useAtom(atom);
	return query;
}

/** A change waiting for the person to confirm what it switches on before Ready. */
interface PendingChange {
	settings: Record<string, ScenarioSetting>;
	drafts: string[];
	notReady: NotReadyItem[];
}

/**
 * Promotes a change of settings: previews it first, and when it switches on a reply or a write that is not Ready,
 * holds it for the person to confirm (`pending`); `confirm` promotes it with those items acknowledged.
 */
export function usePromoteChange(sopId: string) {
	const preview = usePromotionPreview();
	const promote = usePromoteProtocol();
	const [pending, setPending] = useState<PendingChange | null>(null);

	const send = useCallback(
		async (change: Omit<PendingChange, 'notReady'>, acknowledgeNotReady: string[]): Promise<boolean> => {
			try {
				await promote.mutateAsync({ sopId, scenarios: change.settings, drafts: change.drafts, acknowledgeNotReady });
				invalidateQueries(promotionKey('history', sopId));
				return true;
			} catch (err) {
				toast.error(getApiErrorMessage(err, 'Could not promote'));
				return false;
			}
		},
		[promote, sopId]
	);

	const change = useCallback(
		async (settings: Record<string, ScenarioSetting>, drafts: string[] = []): Promise<boolean> => {
			try {
				const { notReady } = await preview.mutateAsync({ sopId, scenarios: settings, drafts });
				if (notReady.length) {
					setPending({ settings, drafts, notReady });
					return false;
				}
			} catch (err) {
				toast.error(getApiErrorMessage(err, 'Could not check the change'));
				return false;
			}
			return send({ settings, drafts }, []);
		},
		[preview, send, sopId]
	);

	const confirm = useCallback(async (): Promise<void> => {
		if (!pending) return;
		const ok = await send(pending, pending.notReady.map((item) => item.key));
		if (ok) setPending(null);
	}, [pending, send]);

	const cancel = useCallback(() => setPending(null), []);

	return { change, pending: pending?.notReady ?? null, confirm, cancel, busy: preview.isPending || promote.isPending };
}
